import type { Severity } from '@vision360/contracts';
import i18n from '@/lib/i18n';

/** fr-FR formatting shared by widgets (TechSpec §8.4). */

const relative = new Intl.RelativeTimeFormat('fr-FR', { numeric: 'auto' });
export const absoluteTime = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'short',
  timeStyle: 'medium',
});

/** "il y a 5 minutes" — the absolute time belongs in a title attribute (NFR-U6). */
export function ago(date: Date | string, now: number = Date.now()): string {
  const seconds = Math.round((new Date(date).getTime() - now) / 1000);
  if (Math.abs(seconds) < 60) return relative.format(seconds, 'second');
  if (Math.abs(seconds) < 3600) return relative.format(Math.round(seconds / 60), 'minute');
  if (Math.abs(seconds) < 86_400) return relative.format(Math.round(seconds / 3600), 'hour');
  return relative.format(Math.round(seconds / 86_400), 'day');
}

const numberFormats = new Map<number, Intl.NumberFormat>();

export function formatNumber(value: number, decimals = 0): string {
  let format = numberFormats.get(decimals);
  if (!format) {
    format = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    numberFormats.set(decimals, format);
  }
  return format.format(value);
}

/** A value with its unit, French-spaced: "42 %", "1,5 Mo/s". Bytes scale. */
export function formatValue(value: number, unit: string | undefined, decimals = 0): string {
  if (unit === 'B' || unit === 'B/s') {
    const steps = ['o', 'Ko', 'Mo', 'Go', 'To'];
    let v = value;
    let i = 0;
    while (Math.abs(v) >= 1024 && i < steps.length - 1) {
      v /= 1024;
      i++;
    }
    return `${formatNumber(v, i === 0 ? 0 : Math.max(decimals, 1))} ${steps[i]}${unit === 'B/s' ? '/s' : ''}`;
  }
  return unit ? `${formatNumber(value, decimals)} ${unit}` : formatNumber(value, decimals);
}

/** "5 min", "1 h", "90 s": the largest whole unit. */
export const formatDuration = (seconds: number): string =>
  seconds % 3600 === 0 && seconds > 0
    ? `${seconds / 3600} h`
    : seconds % 60 === 0 && seconds > 0
      ? `${seconds / 60} min`
      : `${seconds} s`;

/** Severity chip colours, shared by the event feed and the alert rules. */
export const SEVERITY_CLASS: Record<Severity, string> = {
  0: 'bg-muted text-muted-foreground',
  1: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  2: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  3: 'bg-red-500/15 text-red-700 dark:text-red-400',
  4: 'bg-red-600 text-white',
};

/** A label for a tag set: its values, joined. `{}` → '' */
export const tagLabel = (tags: Record<string, string>): string => Object.values(tags).join(' · ');

/**
 * Translation in a widget's own namespace, `widget.<type>` — loaded from its
 * folder by the registry, so it is not in the typed resources.
 */
export function widgetText(type: string) {
  // The typed `t` only knows the bundled namespaces; widget namespaces are added at runtime.
  const t = i18n.t as unknown as (key: string, options?: Record<string, unknown>) => string;
  return (key: string, vars?: Record<string, unknown>) => t(key, { ns: `widget.${type}`, ...vars });
}
